import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import { getTotalPassengerCountFromWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Passengers/PassengerTabHelpers';
import type { IApplicationConfigEntityAttributes } from './Entities';
import { upperCaseFirst } from 'Util/StringUtils';
import { store } from './Store';
import { whiteLabelStore } from './WhiteLabelStore';
import { isWeightUnit, parseWeightUnit, toCanonicalKg, type WeightUnit } from 'Util/Weight';

const isTrue = (value: string) => value.toLowerCase() === 'true';

export type RefundMethod = 'Bank' | 'Credit' | 'Manual';

const ALL_REFUND_METHODS: ReadonlySet<RefundMethod> = new Set(['Bank', 'Credit', 'Manual']);

/**
 * Mirrors `BookingConstants.ABSOLUTE_MAX_BULK_BOOKING_LIMIT`. A bulk booking takes one distributed
 * lock per trip, so the server ignores a configured value above this whatever the admin screen holds.
 */
const BULK_BOOKINGS_ABSOLUTE_MAX_TRIPS = 100;

const parseRefundMethodCsv = (raw: string | undefined): Set<RefundMethod> => {
	if (!raw) return new Set();
	return new Set(
		raw
			.split(',')
			.map(v => v.trim())
			.filter((v): v is RefundMethod => (ALL_REFUND_METHODS as Set<string>).has(v)),
	);
};

export const AppConfigKeys = {
	allowCashInBookingWizard: 'BookingWizard.cashPayment.enabled',
	allowTerminalPayment: 'Payment.terminalPayment.enabled',
	allowEftposPayment: 'Payment.eftposPayment.enabled',
	bookingInvoiceForInvoicedUsers: 'Booking.invoiceForInvoicedUsers.enabled',
	bookingTransferEnabled: 'Booking.transfer.enabled',
	entitlementTransferEnabled: 'Entitlement.transfer.enabled',
	lockChangesWhenCheckInFinalised: 'CheckIn.lockChangesWhenFinalised.enabled',
	canSkipPaymentInCheckInForNonInvoicedUsers: 'Payment.skipPaymentInCheckIn.enabled',
	staffCanSkipPayment: 'Payment.staffSkipPayment.enabled',
	bookingSuccessPromoUrl: 'BookingSuccess.promo.url',
	bookingSuccessPromoVisibleOnAlteration: 'BookingSuccess.promo.visibleOnAlteration',
	bookingSuccessPromoVisibleOnCreation: 'BookingSuccess.promo.visibleOnCreation',
	checkInStatusBookedColour: 'CheckInStatus.booked.colour',
	checkInCanEditDriverNames: 'CheckIn.canEditDriverNames.enabled',
	checkInWarningTimeBuffer: 'CheckIn.WarningTimeBuffer',
	checkInPassengerNamesEnabled: 'CheckIn.passengerNames.enabled',
	checkInWarnOnScannedBookingTypeMismatch: 'CheckIn.warnOnScannedBookingTypeMismatch.enabled',
	reservationHoldMinutes: 'Reservation.holdMinutes',
	defaultPricingNewTripsEnabled: 'DefaultPricing.newFerryTrip.enabled',
	driverPhoneEnabled: 'Driver.phone.enabled',
	driverPhoneRequired: 'Driver.phone.required',
	eventsEnabled: 'Events.enabled',
	voucherPurchaseEnabled: 'Voucher.customerPurchase.enabled',
	voucherHomepageColour: 'Voucher.homepage.colour',
	anytimeTicketingEnabled: 'AnytimeTicketing.enabled',
	anytimeTicketingIconName: 'AnytimeTicketing.iconName',
	anytimeTicketingHomepageColour: 'AnytimeTicketing.homepage.colour',
	homepageFaqEnabled: 'Homepage.FAQ.enabled',
	homepageFaqLink: 'Homepage.FAQ.link',
	eventsPhoneEnabled: 'Events.phone.enabled',
	eventsPhoneRequired: 'Events.phone.required',
	eventsSimpleListViewEnabled: 'Events.simpleListView.enabled',
	experiencesCategoryOptions: 'Experiences.category.options',
	ferryTripImportEnabled: 'FerryTrip.import.enabled',
	ferryTripNoteIconName: 'FerryTrip.noteIconName',
	freightEnabled: 'Freight.enabled',
	freightIconName: 'Freight.iconName',
	charterEnabled: 'Charter.enabled',
	parkingEnabled: 'Parking.enabled',
	capacitySpacesLeftLabelEnabled: 'Capacity.spacesLeftLabel.enabled',
	parkingIconName: 'Parking.iconName',
	parkingHomepageIconColour: 'Parking.homepage.iconColour',
	parkingCustomerCanCreate: 'Parking.CustomerCanCreate.enabled',
	parkingCustomerCanEdit: 'Parking.CustomerCanEdit.enabled',
	parkingCustomerCanCancel: 'Parking.CustomerCanCancel.enabled',
	freightDimensionsHiddenOnStaticPriceEnabled: 'Freight.dimensionsHiddenOnStaticPrice.enabled',
	freightNotifyDetailsCompanynameEnabled: 'Freight.notifydetails.companyname.enabled',
	freightNotifyDetailsCompanynameRequired: 'Freight.notifydetails.companyname.required',
	freightNotifyDetailsEmailEnabled: 'Freight.notifydetails.email.enabled',
	freightNotifyDetailsEmailRequired: 'Freight.notifydetails.email.required',
	freightNotifyDetailsEnabled: 'Freight.notifydetails.enabled',
	freightNotifyDetailsFirstnameEnabled: 'Freight.notifydetails.firstname.enabled',
	freightNotifyDetailsFirstnameRequired: 'Freight.notifydetails.firstname.required',
	freightNotifyDetailsLastnameEnabled: 'Freight.notifydetails.lastname.enabled',
	freightNotifyDetailsLastnameRequired: 'Freight.notifydetails.lastname.required',
	freightNotifyDetailsPhoneEnabled: 'Freight.notifydetails.phone.enabled',
	freightNotifyDetailsPhoneRequired: 'Freight.notifydetails.phone.required',
	freightReceiverDetailsCompanynameEnabled: 'Freight.receiverdetails.companyname.enabled',
	freightReceiverDetailsCompanynameRequired: 'Freight.receiverdetails.companyname.required',
	freightReceiverDetailsEmailEnabled: 'Freight.receiverdetails.email.enabled',
	freightReceiverDetailsEmailRequired: 'Freight.receiverdetails.email.required',
	freightReceiverDetailsEnabled: 'Freight.receiverdetails.enabled',
	freightReceiverDetailsFirstnameEnabled: 'Freight.receiverdetails.firstname.enabled',
	freightReceiverDetailsFirstnameRequired: 'Freight.receiverdetails.firstname.required',
	freightReceiverDetailsLastnameEnabled: 'Freight.receiverdetails.lastname.enabled',
	freightReceiverDetailsLastnameRequired: 'Freight.receiverdetails.lastname.required',
	freightReceiverDetailsPhoneEnabled: 'Freight.receiverdetails.phone.enabled',
	freightReceiverDetailsPhoneRequired: 'Freight.receiverdetails.phone.required',
	gstLabel: 'GST.label',
	guestBookingsEnabled: 'GuestBookings.enabled',
	migratedBookingsModificationEnabled: 'Migration.bookings.modification.enabled',
	seedingExportEnabled: 'Seeding.export.enabled',
	migratedBookingsModificationInfoBody: 'Migration.bookings.modification.info.body',
	migratedBookingsModificationInfoTitle: 'Migration.bookings.modification.info.title',
	passengerAddressEnabled: 'Passenger.address.enabled',
	passengerAddressRequired: 'Passenger.address.required',
	passengerCapacityVisibleToCustomers: 'Passenger.capacityVisibleToCustomers',
	capacityOrderVehicle: 'Capacity.order.vehicle',
	capacityOrderTrailer: 'Capacity.order.trailer',
	capacityOrderPassenger: 'Capacity.order.passenger',
	capacityOrderFreight: 'Capacity.order.freight',
	passengerAgeEnabled: 'Passenger.age.enabled',
	passengerAgeRequired: 'Passenger.age.required',
	passengerDateofbirthEnabled: 'Passenger.dateofbirth.enabled',
	passengerDateofbirthRequired: 'Passenger.dateofbirth.required',
	passengerEmailEnabled: 'Passenger.email.enabled',
	passengerEmailRequired: 'Passenger.email.required',
	passengerFirstnameEnabled: 'Passenger.firstname.enabled',
	passengerFirstnameRequired: 'Passenger.firstname.required',
	passengerGenderEnabled: 'Passenger.gender.enabled',
	passengerGenderOptions: 'Passenger.gender.options',
	passengerGenderRequired: 'Passenger.gender.required',
	passengerInfoEnabled: 'Passenger.passengerInfo.enabled',
	passengerClassesEnabled: 'Passenger.passengerClasses.enabled',
	passengerLastnameEnabled: 'Passenger.lastname.enabled',
	passengerLastnameRequired: 'Passenger.lastname.required',
	passengerPhoneEnabled: 'Passenger.phone.enabled',
	passengerPhoneRequired: 'Passenger.phone.required',
	passengerInfoInCheckInEnabled: 'Passenger.passengerInfoInCheckIn.enabled',
	passengerInfoInCheckInRequired: 'Passenger.passengerInfoInCheckIn.required',
	reportRefundsEmailRecipients: 'Report.refunds.emailRecipients',
	showVoyageNumberInput: 'VoyageNumber.enabled',
	standbyEnabled: 'Standby.enabled',
	standbyMaxCountPerTrip: 'Standby.MaxCountPerTrip',
	standbyOpenHoursBeforeDeparture: 'Standby.OpenHoursBeforeDeparture',
	standbyAuthorizedUserTypes: 'Standby.AuthorizedUserTypes',
	standbyCargoCharacteristicsWatchlist: 'Standby.CargoCharacteristicsWatchlist',
	userPhoneEnabled: 'User.phone.enabled',
	userPhoneRequired: 'User.phone.required',
	userPostcodeEnabled: 'User.postcode.enabled',
	userCompanyNameEnabled: 'User.companyName.enabled',
	vehiclesEnabled: 'Vehicles.enabled',
	voyageNumberLabel: 'VoyageNumber.label',
	voyageNumberPlaceholder: 'VoyageNumber.placeholder',
	voyageNumberRequired: 'VoyageNumber.required',
	voyageNumberShowToCustomers: 'VoyageNumber.showToCustomers',
	widthIncludesDrivePrice: 'Width.IncludesDrivePrice',
	widthVehicleAndTrailerLinked: 'Width.VehicleAndTrailerLinked',
	bookingCollectPrimaryNameEnabled: 'Booking.collectPrimaryName.enabled',
	bookingPrimaryNameFirstNameEnabled: 'Booking.primaryName.firstNameEnabled',
	bookingPrimaryNameLastNameEnabled: 'Booking.primaryName.lastNameEnabled',
	passengerNoteEnabled: 'Passenger.note.enabled',
	passengerNoteRequired: 'Passenger.note.required',
	passengerNoteShowIndicatorInCheckIn: 'Passenger.note.showIndicatorInCheckIn',
	vehicleNoteEnabled: 'Vehicle.note.enabled',
	vehicleNoteRequired: 'Vehicle.note.required',
	vehicleNoteShowIndicatorInCheckIn: 'Vehicle.note.showIndicatorInCheckIn',
	trailersEnabled: 'Trailer.enabled',
	vehicleBookingRequiresAdult: 'Vehicle.passengers.required',
	previousVehicleSelectionEnabled: 'Vehicle.previousVehicleSelection.enabled',
	regoEnabled: 'Vehicle.rego.enabled',
	vehicleShortName: 'Vehicle.shortName',
	vehicleLengthInputEnabled: 'Vehicle.lengthInput.enabled',
	showConfirmationPopUpInCheckIn: 'CheckIn.paymentConfirmationPopUp.enabled',
	allowUnpaidBookings: 'Payment.allowUnpaidBookings.enabled',
	showUnpaidStatus: 'Payment.showUnpaidStatus.enabled',
	blockCheckInUntilPaid: 'CheckIn.blockUntilPaid.enabled',
	bulkBookingsEnabled: 'Booking.bulkBookings.enabled',
	bulkBookingsMaxTrips: 'Booking.bulkBookings.maxTrips',
	bulkCapacityChangeMaxTrips: 'FerryTrip.bulkCapacityChange.maxTrips',
	passengerOnlyBookingsEnabled: 'PassengerOnlyBookings.enabled',
	passengerOnlyCustomerBookingFlowEnabled: 'PassengerOnlyBookings.customerBookingFlowEnabled',
	trailerRemovalFeeEnabled: 'Trailer.removalFee.enabled',
	trailerRemovalFeeUsesVehicleCutoffRules: 'Trailer.removalFee.usesVehicleCutoffRules',
	trailerHasOwnPricing: 'Trailer.hasOwnPricing',
	vehiclePricePassengerCount: 'Vehicle.priceIncludesPassengerCount',
	vehiclePriceIncludedPassengerTypeIds: 'Vehicle.priceIncludesPassengerTypeIds',
	vehiclePriceIncludedPassengerPriority: 'Vehicle.priceIncludesPassengerPriority',
	messageTemplateStaffCanEditContent: 'MessageTemplate.staffCanEditContent',
	smsEnabled: 'Sms.enabled',
	vehicleHasPassengersEnabled: 'Vehicle.passengers.enabled',
	regoScanningEnabled: 'Rego.scanning.enabled',
	twoFactorRequiredOnAllStaffAccounts: 'TwoFactor.allStaffAccounts.required',
	refundsCustomerAllowedMethods: 'Refunds.customerAllowedMethods',
	refundsStaffAllowedMethods: 'Refunds.staffAllowedMethods',
	refundsManagerAllowedMethods: 'Refunds.managerAllowedMethods',
	refundsAdminAllowedMethods: 'Refunds.adminAllowedMethods',
	refundsDefaultMethod: 'Refunds.defaultMethod',
	requireManualRefundNote: 'Refunds.note.required',
	cancellationReasonStaffEnabled: 'Cancellation.reason.staff.enabled',
	cancellationReasonCustomerEnabled: 'Cancellation.reason.customer.enabled',
	reportInvoiceTemplate: 'Report.InvoiceReportTemplate',
	displayCompanyNameInsteadOfName: 'Customer.displayCompanyNameInsteadOfName',
	allowRefundsInCheckIn: 'Refunds.inCheckIn.enabled',
	calendarWeekStartDay: 'CalendarWeek.StartDay',
	driverlessVehicleIncludesOneAdultPrice: 'DriverlessVehicle.IncludesOneAdultPrice',
	ferryScheduleMinimumPriceIncludesOneAdult: 'FerrySchedule.IncludesOneAdultPrice',
	ferryScheduleCondensedViewEnabled: 'FerrySchedule.condensedView.enabled',
	maxNumberOfWaitlistItems: 'Waitlist.maxNumberOfItems',
	showCreditsTabOnCustomerAccount: 'Credits.showOnCustomerAccount',
	allowManualCreditCreation: 'Credits.allowManualCreditCreation',
	preventCreditEmailOnCreditRefundOverride: 'Refunds.preventCreditEmailOnCreditRefundOverride',
	systemWasMigrated: 'System.wasMigrated',
	systemEmailsTransactionReportRecipients: 'SystemEmails.transactionReportRecipients',
	systemEmailsTripCancellationReportRecipients: 'SystemEmails.tripCancellationReportRecipients',
	systemEmailsOverbookedTripsReportRecipients: 'SystemEmails.overbookedTripsReportRecipients',
	systemEmailsFeeChangeRecipients: 'SystemEmails.feeChangeRecipients',
	systemEmailsTripAdditionNotificationRecipients: 'SystemEmails.tripAdditionNotificationRecipients',
	systemEmailsTripCancellationNotificationRecipients: 'SystemEmails.tripCancellationNotificationRecipients',
	systemEmailsTripCapacityChangeNotificationRecipients: 'SystemEmails.tripCapacityChangeNotificationRecipients',
	systemEmailsInvoicedVoucherNotificationEmails: 'SystemEmails.invoicedVoucherNotificationEmails',
	voucherPurchasedExpiryYears: 'Voucher.purchased.expiryYears',
	voucherCreditExpiryYears: 'Voucher.credit.expiryYears',
	voucherGstPercentage: 'Voucher.gst.percentage',
	voucherRedeemText: 'Voucher.redeemText',
	globalBannerEnabled: 'GlobalBanner.enabled',
	globalBannerContent: 'GlobalBanner.content',
	marketingEnabled: 'Marketing.enabled',
	weightDisplayUnit: 'Weight.displayUnit',
	weightExactInputVehicleEnabled: 'Weight.exactInput.vehicleEnabled',
	weightExactInputTrailerEnabled: 'Weight.exactInput.trailerEnabled',
	weightExactInputVehicleRequiredCharacteristicIds: 'Weight.exactInput.vehicleRequiredCharacteristicIds',
	weightExactInputTrailerRequiredCharacteristicIds: 'Weight.exactInput.trailerRequiredCharacteristicIds',
	weightExactInputVehicleMaximumValue: 'Weight.exactInput.vehicleMaximumValue',
	weightExactInputVehicleMaximumUnit: 'Weight.exactInput.vehicleMaximumUnit',
	weightExactInputTrailerMaximumValue: 'Weight.exactInput.trailerMaximumValue',
	weightExactInputTrailerMaximumUnit: 'Weight.exactInput.trailerMaximumUnit',
	weightExactInputCombinedMaximumValue: 'Weight.exactInput.combinedMaximumValue',
	weightExactInputCombinedMaximumUnit: 'Weight.exactInput.combinedMaximumUnit',
} as const;

export class AppConfigStore {
	config: IApplicationConfigEntityAttributes[];

	/**
	 * Indicates whether migrated bookings can be modified.
	 * If false, staff can cancel the booking and issue credit.
	 */
	migratedBookingsModificationEnabled: boolean;
	seedingExportEnabled: boolean;
	migratedBookingsModificationInfoTitle: string;
	migratedBookingsModificationInfoBody: string;

	bookingSuccessPromoVisibleOnCreation: boolean;
	bookingSuccessPromoVisibleOnAlteration: boolean;
	bookingSuccessPromoUrl: string;

	allowCashInBookingWizard: boolean;

	constructor(config: IApplicationConfigEntityAttributes[]) {
		this.config = config;
		this.assignMigratedBookingSettings(config);
		this.assignBookingSuccessSettings(config);
		this.assignSeedingSettings(config);
	}

	private assignSeedingSettings(config: IApplicationConfigEntityAttributes[]) {
		const seedingExportEnabled = config.find(x => x.key === AppConfigKeys.seedingExportEnabled);
		this.seedingExportEnabled = seedingExportEnabled ? isTrue(seedingExportEnabled.value) : false;
	}

	private assignMigratedBookingSettings(config: IApplicationConfigEntityAttributes[]) {
		const migratedBookingsModificationEnabled = config.find(
			x => x.key === AppConfigKeys.migratedBookingsModificationEnabled,
		);
		this.migratedBookingsModificationEnabled = migratedBookingsModificationEnabled
			? isTrue(migratedBookingsModificationEnabled.value)
			: false;

		const migratedBookingsModificationInfoTitle = config.find(
			x => x.key === AppConfigKeys.migratedBookingsModificationInfoTitle,
		);
		this.migratedBookingsModificationInfoTitle = migratedBookingsModificationInfoTitle
			? String(migratedBookingsModificationInfoTitle.value)
			: 'Booking is closed for modifications';

		const migratedBookingsModificationInfoBody = config.find(
			x => x.key === AppConfigKeys.migratedBookingsModificationInfoBody,
		);
		this.migratedBookingsModificationInfoBody = migratedBookingsModificationInfoBody
			? String(migratedBookingsModificationInfoBody.value)
			: "This booking can no longer be modified. If you'd like to modify your booking, please reach out to our staff for assistance.";
	}

	private assignBookingSuccessSettings(config: IApplicationConfigEntityAttributes[]) {
		const bookingSuccessPromoVisibleOnCreation = config.find(
			x => x.key === AppConfigKeys.bookingSuccessPromoVisibleOnCreation,
		);
		this.bookingSuccessPromoVisibleOnCreation = bookingSuccessPromoVisibleOnCreation
			? isTrue(bookingSuccessPromoVisibleOnCreation.value)
			: false;

		const bookingSuccessPromoVisibleOnAlteration = config.find(
			x => x.key === AppConfigKeys.bookingSuccessPromoVisibleOnAlteration,
		);
		this.bookingSuccessPromoVisibleOnAlteration = bookingSuccessPromoVisibleOnAlteration
			? isTrue(bookingSuccessPromoVisibleOnAlteration.value)
			: false;

		const bookingSuccessPromoUrl = config.find(x => x.key === AppConfigKeys.bookingSuccessPromoUrl);
		this.bookingSuccessPromoUrl = bookingSuccessPromoUrl ? String(bookingSuccessPromoUrl.value) : '/';
	}

	public isConfigEnabled(key: string) {
		const match = this.config?.find(x => x.key === key);
		if (!match) {
			return false;
		}
		return match.value.toLowerCase() === 'true';
	}

	/** Read a numeric config value, returning `fallback` when missing or not a number. */
	public getNumberConfig(key: string, fallback: number): number {
		const raw = this.config?.find(x => x.key === key)?.value;
		const parsed = Number(raw);
		return Number.isFinite(parsed) ? parsed : fallback;
	}

	/**
	 * Minutes either side of a trip's departure within which no wrong-check-in warning is shown.
	 * When the current time falls outside this window on the check-in summary, staff are warned.
	 * A value of 0 (or less) disables the warning entirely.
	 */
	public get checkInWarningTimeBuffer() {
		return this.getNumberConfig(AppConfigKeys.checkInWarningTimeBuffer, 30);
	}

	/** Passenger names behind a check-in row's chevron. Fare classes show the list regardless. */
	public get checkInPassengerNamesEnabled() {
		return this.isConfigEnabled(AppConfigKeys.checkInPassengerNamesEnabled);
	}

	/** Warn on a scanner result whose booking type is not the type of the active check-in tab. */
	public get checkInWarnOnScannedBookingTypeMismatch() {
		return this.isConfigEnabled(AppConfigKeys.checkInWarnOnScannedBookingTypeMismatch);
	}

	/**
	 * How long a started-but-unpaid transaction holds its space. Mirrors the serverside
	 * `IWhiteLabelService.ReservationHoldMinutesAsync()`, including its floor of 1 — a hold that has
	 * already expired when it is created would have the countdown start at zero.
	 *
	 * The reservation countdown reads this to decide when to start offering an extension, so that a
	 * tenant on a short hold is not offered one from the moment the timer appears.
	 */
	public get reservationHoldMinutes(): number {
		return Math.max(1, this.getNumberConfig(AppConfigKeys.reservationHoldMinutes, 15));
	}

	// Capacity display order on trip cards (lower = first). Defaults: vehicle → trailer → passenger → freight.
	public get capacityOrderVehicle() {
		return this.getNumberConfig(AppConfigKeys.capacityOrderVehicle, 1);
	}

	public get capacityOrderTrailer() {
		return this.getNumberConfig(AppConfigKeys.capacityOrderTrailer, 2);
	}

	public get capacityOrderPassenger() {
		return this.getNumberConfig(AppConfigKeys.capacityOrderPassenger, 3);
	}

	public get capacityOrderFreight() {
		return this.getNumberConfig(AppConfigKeys.capacityOrderFreight, 4);
	}

	// Passenger info config
	/**
	 * Fare classes and seat selection. Off by default: with it off the wizard's seating step is
	 * excluded, no class tags appear on trip cards, and pricing resolves exactly as it did before
	 * classes existed.
	 */
	public get passengerClassesEnabled() {
		return this.isConfigEnabled(AppConfigKeys.passengerClassesEnabled);
	}

	public get passengerInfoEnabled() {
		const fields = [
			this.passengerFirstNameEnabled,
			this.passengerLastNameEnabled,
			this.passengerPhoneEnabled,
			this.passengerEmailEnabled,
			this.passengerAgeEnabled,
			this.passengerDateOfBirthEnabled,
			this.passengerAddressEnabled,
			this.passengerGenderEnabled,
		];
		return this.isConfigEnabled(AppConfigKeys.passengerInfoEnabled) && fields.includes(true);
	}

	public showPassengerTab(wizardData: BookingWizardData) {
		const { tabSelected } = wizardData;
		const onVehicleTab = tabSelected === 'vehicle';
		const onPassengerTab = tabSelected === 'passenger';
		const passengerCount = getTotalPassengerCountFromWizardData(wizardData);

		return this.passengerInfoEnabled && (onPassengerTab || (onVehicleTab && passengerCount > 0));
	}

	/**
	 * Whether staff-facing surfaces show the customer's company name in place of their personal
	 * name (falling back to the personal name when they have no company). Staff UI only — does not
	 * affect reports. Read through the `getCustomerDisplayName` helper rather than directly.
	 */
	public get displayCompanyNameInsteadOfName() {
		return this.isConfigEnabled(AppConfigKeys.displayCompanyNameInsteadOfName);
	}

	public get passengerFirstNameEnabled() {
		return this.isConfigEnabled(AppConfigKeys.passengerFirstnameEnabled);
	}

	public get passengerFirstNameRequired() {
		return this.isConfigEnabled(AppConfigKeys.passengerFirstnameRequired);
	}

	public get passengerLastNameEnabled() {
		return this.isConfigEnabled(AppConfigKeys.passengerLastnameEnabled);
	}

	public get passengerLastNameRequired() {
		return this.isConfigEnabled(AppConfigKeys.passengerLastnameRequired);
	}

	public get passengerPhoneEnabled() {
		return this.isConfigEnabled(AppConfigKeys.passengerPhoneEnabled);
	}

	public get passengerPhoneRequired() {
		return this.isConfigEnabled(AppConfigKeys.passengerPhoneRequired);
	}

	public get passengerEmailEnabled() {
		return this.isConfigEnabled(AppConfigKeys.passengerEmailEnabled);
	}

	public get passengerEmailRequired() {
		return this.isConfigEnabled(AppConfigKeys.passengerEmailRequired);
	}

	public get passengerAgeEnabled() {
		return this.isConfigEnabled(AppConfigKeys.passengerAgeEnabled);
	}

	public get passengerAgeRequired() {
		return this.isConfigEnabled(AppConfigKeys.passengerAgeRequired);
	}

	public get passengerGenderEnabled() {
		return this.isConfigEnabled(AppConfigKeys.passengerGenderEnabled) && this.passengerGenderOptions.length > 0;
	}

	public get passengerGenderRequired() {
		return this.isConfigEnabled(AppConfigKeys.passengerGenderRequired);
	}

	public get passengerGenderOptions() {
		return (
			this.config
				?.find(x => x.key === AppConfigKeys.passengerGenderOptions)
				?.value.split(',')
				.map(y => upperCaseFirst(y.trim().toLowerCase())) ?? []
		);
	}

	public get passengerAddressEnabled() {
		return this.isConfigEnabled(AppConfigKeys.passengerAddressEnabled);
	}

	/**
	 * Whether customers may see the remaining passenger-capacity numbers. When false, customers
	 * see only a green/red availability indicator; staff always see the numbers.
	 */
	public get passengerCapacityVisibleToCustomers() {
		return this.isConfigEnabled(AppConfigKeys.passengerCapacityVisibleToCustomers);
	}

	public get passengerAddressRequired() {
		return this.isConfigEnabled(AppConfigKeys.passengerAddressRequired);
	}

	public get passengerDateOfBirthEnabled() {
		return this.isConfigEnabled(AppConfigKeys.passengerDateofbirthEnabled);
	}

	public get passengerDateOfBirthRequired() {
		return this.isConfigEnabled(AppConfigKeys.passengerDateofbirthRequired);
	}

	// Count of booking types shown in the standard booking flow (wizard search step + sidebar).
	// Walk-on appears whenever the passenger option is offered to the viewer, so the count mirrors
	// that to keep the `booking-type-single-line` styling in sync with what renders.
	public get oddNumberOfBookingTypes() {
		let count = 0;
		if (this.showPassengerBookingOption) {
			count++;
		}
		if (this.vehicleEnabled) {
			count++;
		}
		if (this.freightEnabled) {
			count++;
		}
		return count % 2 === 1;
	}

	// Count of booking types shown at check-in, where walk-on appears whenever passenger-only
	// bookings are enabled (independent of the booking flow flag).
	public get oddNumberOfBookingTypesCheckIn() {
		let count = 0;
		if (this.passengerOnlyBookingEnabled) {
			count++;
		}
		if (this.vehicleEnabled) {
			count++;
		}
		if (this.freightEnabled) {
			count++;
		}
		return count % 2 === 1;
	}

	public get vehicleEnabled() {
		return this.isConfigEnabled(AppConfigKeys.vehiclesEnabled);
	}

	public get vehicleWidthEnabled() {
		return store.widths.length > 0;
	}

	/**
	 * When enabled, the vehicle and trailer share a single width — the trailer follows the
	 * vehicle's width rather than having its own. Defaults to enabled.
	 */
	public get vehicleAndTrailerWidthLinked() {
		return this.isConfigEnabled(AppConfigKeys.widthVehicleAndTrailerLinked);
	}

	public get driverPhoneEnabled() {
		return this.isConfigEnabled(AppConfigKeys.driverPhoneEnabled);
	}

	public get driverPhoneRequired() {
		return this.isConfigEnabled(AppConfigKeys.driverPhoneRequired);
	}

	/**
	 * Whether the ferry schedule's trip cards carry a "Space left" label above their capacity tags.
	 * On by default; turning it off drops the line and shortens every card, and the tags still label
	 * themselves.
	 */
	public get capacitySpacesLeftLabelEnabled() {
		return this.isConfigEnabled(AppConfigKeys.capacitySpacesLeftLabelEnabled);
	}

	// Charter config
	public get charterEnabled() {
		return this.isConfigEnabled(AppConfigKeys.charterEnabled);
	}

	// Parking config
	/**
	 * The Material Symbols icon name used for parking. Falls back to `local_parking` when unset or
	 * blank, the same way `freightIconName` does — an empty config row must not render an empty tile.
	 */
	public get parkingIconName() {
		const value = this.config?.find(x => x.key === AppConfigKeys.parkingIconName)?.value?.trim();
		return value || 'local_parking';
	}

	/**
	 * Hex fill for the parking icon's square on the homepage booking card. Empty when unset, which
	 * leaves the square on its default translucent fill — the same "blank means default" contract the
	 * voucher and anytime ticket card colours use.
	 */
	public get parkingHomepageIconColour() {
		return this.config?.find(x => x.key === AppConfigKeys.parkingHomepageIconColour)?.value ?? '';
	}

	public get parkingEnabled() {
		return this.isConfigEnabled(AppConfigKeys.parkingEnabled);
	}

	public get parkingCustomerCanCreate() {
		const match = this.config?.find(x => x.key === AppConfigKeys.parkingCustomerCanCreate);
		if (!match) return true;
		return match.value.toLowerCase() === 'true';
	}

	public get parkingCustomerCanEdit() {
		const match = this.config?.find(x => x.key === AppConfigKeys.parkingCustomerCanEdit);
		if (!match) return true;
		return match.value.toLowerCase() === 'true';
	}

	public get parkingCustomerCanCancel() {
		const match = this.config?.find(x => x.key === AppConfigKeys.parkingCustomerCanCancel);
		if (!match) return true;
		return match.value.toLowerCase() === 'true';
	}

	// Freight config
	public get freightEnabled() {
		return this.isConfigEnabled(AppConfigKeys.freightEnabled);
	}

	public get freightDimensionsHiddenOnStaticPriceEnabled() {
		return this.isConfigEnabled(AppConfigKeys.freightDimensionsHiddenOnStaticPriceEnabled);
	}

	/**
	 * Material Symbols name for the icon shown against a ferry trip's notes. Falls back when unset or
	 * blank, the same way `freightIconName` and `parkingIconName` do — an empty config row must not
	 * render an empty glyph.
	 */
	public get ferryTripNoteIconName() {
		const value = this.config?.find(x => x.key === AppConfigKeys.ferryTripNoteIconName)?.value?.trim();
		return value || 'info';
	}

	public get freightIconName() {
		const value = this.config?.find(x => x.key === AppConfigKeys.freightIconName)?.value?.trim();
		return value || 'local_shipping';
	}

	public get freightReceiverDetailsEnabled() {
		const fields = [
			this.freightReceiverDetailsFirstNameEnabled,
			this.freightReceiverDetailsLastNameEnabled,
			this.freightReceiverDetailsPhoneEnabled,
			this.freightReceiverDetailsEmailEnabled,
			this.freightReceiverDetailsCompanyNameEnabled,
		];
		return this.isConfigEnabled(AppConfigKeys.freightReceiverDetailsEnabled) && fields.includes(true);
	}

	public get freightReceiverDetailsRequired() {
		const fields = [
			this.freightReceiverDetailsFirstNameRequired,
			this.freightReceiverDetailsLastNameRequired,
			this.freightReceiverDetailsPhoneRequired,
			this.freightReceiverDetailsEmailRequired,
			this.freightReceiverDetailsCompanyNameRequired,
		];
		return this.isConfigEnabled(AppConfigKeys.freightReceiverDetailsEnabled) && fields.includes(true);
	}

	public get freightReceiverDetailsFirstNameEnabled() {
		return this.isConfigEnabled(AppConfigKeys.freightReceiverDetailsFirstnameEnabled);
	}

	public get freightReceiverDetailsFirstNameRequired() {
		return this.isConfigEnabled(AppConfigKeys.freightReceiverDetailsFirstnameRequired);
	}

	public get freightReceiverDetailsLastNameEnabled() {
		return this.isConfigEnabled(AppConfigKeys.freightReceiverDetailsLastnameEnabled);
	}

	public get freightReceiverDetailsLastNameRequired() {
		return this.isConfigEnabled(AppConfigKeys.freightReceiverDetailsLastnameRequired);
	}

	public get freightReceiverDetailsPhoneEnabled() {
		return this.isConfigEnabled(AppConfigKeys.freightReceiverDetailsPhoneEnabled);
	}

	public get freightReceiverDetailsPhoneRequired() {
		return this.isConfigEnabled(AppConfigKeys.freightReceiverDetailsPhoneRequired);
	}

	public get freightReceiverDetailsEmailEnabled() {
		return this.isConfigEnabled(AppConfigKeys.freightReceiverDetailsEmailEnabled);
	}

	public get freightReceiverDetailsEmailRequired() {
		return this.isConfigEnabled(AppConfigKeys.freightReceiverDetailsEmailRequired);
	}

	public get freightReceiverDetailsCompanyNameEnabled() {
		return this.isConfigEnabled(AppConfigKeys.freightReceiverDetailsCompanynameEnabled);
	}

	public get freightReceiverDetailsCompanyNameRequired() {
		return this.isConfigEnabled(AppConfigKeys.freightReceiverDetailsCompanynameRequired);
	}

	public get freightNotifyDetailsEnabled() {
		const fields = [
			this.freightNotifyDetailsFirstNameEnabled,
			this.freightNotifyDetailsLastNameEnabled,
			this.freightNotifyDetailsPhoneEnabled,
			this.freightNotifyDetailsEmailEnabled,
			this.freightNotifyDetailsCompanyNameEnabled,
		];
		return this.isConfigEnabled(AppConfigKeys.freightNotifyDetailsEnabled) && fields.includes(true);
	}

	public get freightNotifyDetailsRequired() {
		const fields = [
			this.freightNotifyDetailsFirstNameRequired,
			this.freightNotifyDetailsLastNameRequired,
			this.freightNotifyDetailsPhoneRequired,
			this.freightNotifyDetailsEmailRequired,
			this.freightNotifyDetailsCompanyNameRequired,
		];
		return this.isConfigEnabled(AppConfigKeys.freightNotifyDetailsEnabled) && fields.includes(true);
	}

	public get freightNotifyDetailsFirstNameEnabled() {
		return this.isConfigEnabled(AppConfigKeys.freightNotifyDetailsFirstnameEnabled);
	}

	public get freightNotifyDetailsFirstNameRequired() {
		return this.isConfigEnabled(AppConfigKeys.freightNotifyDetailsFirstnameRequired);
	}

	public get freightNotifyDetailsLastNameEnabled() {
		return this.isConfigEnabled(AppConfigKeys.freightNotifyDetailsLastnameEnabled);
	}

	public get freightNotifyDetailsLastNameRequired() {
		return this.isConfigEnabled(AppConfigKeys.freightNotifyDetailsLastnameRequired);
	}

	public get freightNotifyDetailsPhoneEnabled() {
		return this.isConfigEnabled(AppConfigKeys.freightNotifyDetailsPhoneEnabled);
	}

	public get freightNotifyDetailsPhoneRequired() {
		return this.isConfigEnabled(AppConfigKeys.freightNotifyDetailsPhoneRequired);
	}

	public get freightNotifyDetailsEmailEnabled() {
		return this.isConfigEnabled(AppConfigKeys.freightNotifyDetailsEmailEnabled);
	}

	public get freightNotifyDetailsEmailRequired() {
		return this.isConfigEnabled(AppConfigKeys.freightNotifyDetailsEmailRequired);
	}

	public get freightNotifyDetailsCompanyNameEnabled() {
		return this.isConfigEnabled(AppConfigKeys.freightNotifyDetailsCompanynameEnabled);
	}

	public get freightNotifyDetailsCompanyNameRequired() {
		return this.isConfigEnabled(AppConfigKeys.freightNotifyDetailsCompanynameRequired);
	}

	public getExperienceCategories() {
		const categories =
			this.config
				?.find(x => x.key === AppConfigKeys.experiencesCategoryOptions)
				?.value.split(',')
				.sort((a, b) => a.localeCompare(b))
				.map(y => upperCaseFirst(y.toLowerCase())) ?? [];

		categories.unshift('All');
		return categories;
	}

	public allowCashPaymentsInBookingWizard() {
		const allowCashInBW = this.config.find(x => x.key === AppConfigKeys.allowCashInBookingWizard);
		return allowCashInBW ? isTrue(allowCashInBW.value) : false;
	}

	public allowTerminalPayments() {
		const allowTerminalPayment = this.config.find(x => x.key === AppConfigKeys.allowTerminalPayment);
		return allowTerminalPayment ? isTrue(allowTerminalPayment.value) : false;
	}

	/**
	 * Whether Eftpos is offered as a payment method. Eftpos is only ever shown to staff taking
	 * payment in check-in, so turning this off removes it from every payment form.
	 * Defaults to true when the config row is missing.
	 */
	public allowEftposPayments() {
		const allowEftposPayment = this.config.find(x => x.key === AppConfigKeys.allowEftposPayment);
		return allowEftposPayment ? isTrue(allowEftposPayment.value) : true;
	}

	public invoicesForInvoicedUsers() {
		return this.isConfigEnabled(AppConfigKeys.bookingInvoiceForInvoicedUsers);
	}

	public get canSkipPaymentInCheckInForNonInvoicedUsers() {
		return this.isConfigEnabled(AppConfigKeys.canSkipPaymentInCheckInForNonInvoicedUsers);
	}

	public get eventsPhoneEnabled() {
		return this.isConfigEnabled(AppConfigKeys.eventsPhoneEnabled);
	}

	public get eventsPhoneRequired() {
		return this.isConfigEnabled(AppConfigKeys.eventsPhoneRequired);
	}

	public get eventsSimpleListViewEnabled() {
		return this.isConfigEnabled(AppConfigKeys.eventsSimpleListViewEnabled);
	}

	public get gstLabel() {
		return this.config?.find(x => x.key === AppConfigKeys.gstLabel)?.value ?? 'GST';
	}

	public get showVoyageNumberInput() {
		return this.isConfigEnabled('VoyageNumber.enabled');
	}

	public get voyageNumberLabel() {
		return this.config?.find(x => x.key === AppConfigKeys.voyageNumberLabel)?.value ?? 'Voyage number';
	}

	public get voyageNumberPlaceholder() {
		return this.config?.find(x => x.key === AppConfigKeys.voyageNumberPlaceholder)?.value ?? 'Enter voyage number';
	}

	public get voyageNumberRequired() {
		return this.isConfigEnabled(AppConfigKeys.voyageNumberRequired);
	}

	/** Whether the voyage number/name is shown to customers. Defaults to true when unset. Staff always see it regardless. */
	public get voyageNumberShowToCustomers() {
		const match = this.config?.find(x => x.key === AppConfigKeys.voyageNumberShowToCustomers);
		return match ? match.value.toLowerCase() === 'true' : true;
	}

	public get bookedStatusTagColour() {
		return this.config?.find(x => x.key === AppConfigKeys.checkInStatusBookedColour)?.value ?? '#81C784';
	}

	public get userPostcodeEnabled() {
		return this.isConfigEnabled(AppConfigKeys.userPostcodeEnabled);
	}

	public get userPhoneEnabled() {
		return this.isConfigEnabled(AppConfigKeys.userPhoneEnabled);
	}

	public get userPhoneRequired() {
		return this.isConfigEnabled(AppConfigKeys.userPhoneRequired);
	}

	/**
	 * Whether the registration forms offer a "Register as a company" option. Ticking it reveals a
	 * required company name input and relabels the name inputs as the contact's name. Separate from
	 * the per-customer-type company name fields on UserDisplayName, which only staff assign.
	 */
	public get userCompanyNameEnabled() {
		return this.isConfigEnabled(AppConfigKeys.userCompanyNameEnabled);
	}






	public get ferryTripImportEnabled() {
		return this.isConfigEnabled(AppConfigKeys.ferryTripImportEnabled);
	}

	public get defaultPricingNewTripsEnabled() {
		return this.isConfigEnabled(AppConfigKeys.defaultPricingNewTripsEnabled);
	}

	public get refundsCustomerAllowedMethods(): Set<RefundMethod> {
		return parseRefundMethodCsv(
			this.config?.find(x => x.key === AppConfigKeys.refundsCustomerAllowedMethods)?.value,
		);
	}

	public get refundsStaffAllowedMethods(): Set<RefundMethod> {
		return parseRefundMethodCsv(this.config?.find(x => x.key === AppConfigKeys.refundsStaffAllowedMethods)?.value);
	}

	public get refundsManagerAllowedMethods(): Set<RefundMethod> {
		return parseRefundMethodCsv(
			this.config?.find(x => x.key === AppConfigKeys.refundsManagerAllowedMethods)?.value,
		);
	}

	public get refundsAdminAllowedMethods(): Set<RefundMethod> {
		return parseRefundMethodCsv(this.config?.find(x => x.key === AppConfigKeys.refundsAdminAllowedMethods)?.value);
	}

	// Resolves the refund methods the actor can choose from. Roles inherit downwards:
	// customer < staff < manager < admin (super = admin). A staff member always gets
	// customer methods plus their own; a manager gets customer + staff + manager; an
	// admin/super gets the union of all four. Callers pass `userIsStaff=true` for
	// staff-side flows; the actual roles held by the logged-in user determine which
	// tiers are unioned in.
	public allowedRefundMethodsFor(userIsStaff: boolean): Set<RefundMethod> {
		const methods = new Set<RefundMethod>(this.refundsCustomerAllowedMethods);
		if (!userIsStaff) return methods;
		this.refundsStaffAllowedMethods.forEach(m => methods.add(m));
		if (store.isManager) {
			this.refundsManagerAllowedMethods.forEach(m => methods.add(m));
		}
		if (store.isAdmin) {
			this.refundsAdminAllowedMethods.forEach(m => methods.add(m));
		}
		return methods;
	}

	// The refund method the tenant prefers when a refund is required without an explicit
	// user choice (alteration flow, default selection in cancellation flow). Falls back to
	// Credit when the stored value is missing or unrecognised.
	public get refundsDefaultMethod(): RefundMethod {
		const raw = this.config?.find(x => x.key === AppConfigKeys.refundsDefaultMethod)?.value?.trim();
		if (raw === 'Bank' || raw === 'Credit' || raw === 'Manual') return raw;
		return 'Credit';
	}

	// Resolves the default refund method for an alteration refund. If the tenant-configured
	// default isn't in the actor's allowed set (e.g. default=Bank but customers only have
	// Credit), falls back to Bank → Manual → Credit so an alteration only issues store credit
	// when the actor has no money-back method. The backend re-resolves this authoritatively
	// (see PaymentController.SetupPayment); this stays in sync for any client-side use.
	// Cancellation flows have their own picker, so this is intentionally alteration-only.
	public resolveAlterationDefaultRefundMethod(userIsStaff: boolean): RefundMethod {
		const allowed = this.allowedRefundMethodsFor(userIsStaff);
		const preferred = this.refundsDefaultMethod;
		if (allowed.has(preferred)) return preferred;
		if (allowed.has('Bank')) return 'Bank';
		if (allowed.has('Manual')) return 'Manual';
		return 'Credit';
	}

	// Union of all role configs — useful for admin-side gating where the question is "does
	// any user type have access to bank/manual refunds?" rather than "what can this user do?"
	public get refundsAnyRoleAllowedMethods(): Set<RefundMethod> {
		const union = new Set<RefundMethod>();
		this.refundsCustomerAllowedMethods.forEach(m => union.add(m));
		this.refundsStaffAllowedMethods.forEach(m => union.add(m));
		this.refundsManagerAllowedMethods.forEach(m => union.add(m));
		this.refundsAdminAllowedMethods.forEach(m => union.add(m));
		return union;
	}

	public get bookingNoteEnabled() {
		return (
			this.isConfigEnabled(AppConfigKeys.passengerNoteEnabled) ||
			this.isConfigEnabled(AppConfigKeys.vehicleNoteEnabled)
		);
	}

	public get bookingTransferEnabled() {
		return this.isConfigEnabled(AppConfigKeys.bookingTransferEnabled);
	}

	public get entitlementTransferEnabled() {
		return this.isConfigEnabled(AppConfigKeys.entitlementTransferEnabled);
	}

	/**
	 * When true, finalising a trip's check-in locks it: bookings on the trip can't be altered and
	 * its check-in status can't be changed until the trip is un-finalised. Enforced on the backend;
	 * exposed here so the UI can proactively disable the relevant controls.
	 */
	public get lockChangesWhenCheckInFinalised() {
		return this.isConfigEnabled(AppConfigKeys.lockChangesWhenCheckInFinalised);
	}

	public get passengerNoteEnabled() {
		return this.isConfigEnabled(AppConfigKeys.passengerNoteEnabled);
	}

	public get passengerNoteRequired() {
		return this.isConfigEnabled(AppConfigKeys.passengerNoteRequired);
	}

	public get vehicleNoteEnabled() {
		return this.isConfigEnabled(AppConfigKeys.vehicleNoteEnabled);
	}

	public get vehicleNoteRequired() {
		return this.isConfigEnabled(AppConfigKeys.vehicleNoteRequired);
	}

	public get vehicleNoteShowIndicatorInCheckIn() {
		return this.isConfigEnabled(AppConfigKeys.vehicleNoteShowIndicatorInCheckIn);
	}

	public get passengerNoteShowIndicatorInCheckIn() {
		return this.isConfigEnabled(AppConfigKeys.passengerNoteShowIndicatorInCheckIn);
	}

	public get collectPrimaryBookingNameEnabled() {
		return this.isConfigEnabled(AppConfigKeys.bookingCollectPrimaryNameEnabled);
	}

	public get primaryBookingFirstNameEnabled() {
		return this.isConfigEnabled(AppConfigKeys.bookingPrimaryNameFirstNameEnabled);
	}

	public get primaryBookingLastNameEnabled() {
		return this.isConfigEnabled(AppConfigKeys.bookingPrimaryNameLastNameEnabled);
	}

	/**
	 * Whether the "name on booking" (primary contact) section should be collected/displayed at all.
	 * Requires the collect-primary-name feature AND at least one of the first/last name fields to be
	 * enabled — with both name fields off there's nothing to capture or show, so the section is hidden
	 * (in the booking wizard and at check-in) even though the feature is technically on.
	 */
	public get primaryBookingNameEnabled() {
		return (
			this.collectPrimaryBookingNameEnabled &&
			(this.primaryBookingFirstNameEnabled || this.primaryBookingLastNameEnabled)
		);
	}

	public get eventsEnabled() {
		return this.isConfigEnabled(AppConfigKeys.eventsEnabled);
	}

	public get voucherPurchaseEnabled() {
		return this.isConfigEnabled(AppConfigKeys.voucherPurchaseEnabled);
	}

	public get voucherHomepageColour() {
		return this.config?.find(x => x.key === AppConfigKeys.voucherHomepageColour)?.value ?? '';
	}

	public get homepageFaqEnabled() {
		return this.isConfigEnabled(AppConfigKeys.homepageFaqEnabled);
	}

	public get homepageFaqLink() {
		return this.config?.find(x => x.key === AppConfigKeys.homepageFaqLink)?.value ?? '';
	}

	public get anytimeTicketingEnabled() {
		return this.isConfigEnabled(AppConfigKeys.anytimeTicketingEnabled);
	}

	/**
	 * The Material Symbols icon name used for anytime tickets. Falls back to `confirmation_number`
	 * when unset or blank, the same way `parkingIconName` and `freightIconName` do — an empty config
	 * row must not render an empty square.
	 */
	public get anytimeTicketingIconName() {
		const value = this.config?.find(x => x.key === AppConfigKeys.anytimeTicketingIconName)?.value?.trim();
		return value || 'confirmation_number';
	}

	public get anytimeTicketingHomepageColour() {
		return this.config?.find(x => x.key === AppConfigKeys.anytimeTicketingHomepageColour)?.value ?? '';
	}

	public get bulkBookingsEnabled() {
		return this.isConfigEnabled(AppConfigKeys.bulkBookingsEnabled);
	}

	/**
	 * The most trips one bulk booking may cover. Use this only before a search has run — the preview
	 * response carries the server's own `maxTrips`, which is authoritative once you have it.
	 *
	 * Clamped the same way `BookingConstants.ResolveBulkBookingMaxTrips` clamps it serverside, so the
	 * "A maximum of {max} bulk bookings can be made" line on Find trips cannot promise a number the
	 * next step then refuses. The admin form holds new values to 20–100, but a value stored before
	 * that range existed is still in the database until the next deploy re-seeds it.
	 */
	/**
	 * The most trips the bulk "Change ferry / capacity profile" action takes at once, for the check
	 * before its preview runs. Clamped as `FerryTripRecurrence.ResolveMaxTrips` clamps it serverside
	 * (1–1000, default 400); the preview response's own `maxTrips` is authoritative once it is back.
	 */
	public get bulkCapacityChangeMaxTrips() {
		return Math.min(1000, Math.max(1, this.getNumberConfig(AppConfigKeys.bulkCapacityChangeMaxTrips, 400)));
	}

	public get bulkBookingsMaxTrips() {
		return Math.min(
			BULK_BOOKINGS_ABSOLUTE_MAX_TRIPS,
			Math.max(1, this.getNumberConfig(AppConfigKeys.bulkBookingsMaxTrips, 20)),
		);
	}

	public get vehicleLengthEnabled() {
		return this.isConfigEnabled(AppConfigKeys.vehicleLengthInputEnabled);
	}

	public get regoEnabled() {
		return this.isConfigEnabled(AppConfigKeys.regoEnabled);
	}

	public get previousVehicleSelectionEnabled() {
		return this.isConfigEnabled(AppConfigKeys.previousVehicleSelectionEnabled);
	}

	public get vehicleShortName() {
		return this.config?.find(x => x.key === AppConfigKeys.vehicleShortName)?.value ?? 'VH';
	}

	/**
	 * Whether a staff member may rewrite a message before sending it, or must send one of the
	 * pre-defined templates as written.
	 *
	 * Managers and above are never restricted — they can edit the templates themselves, so locking them
	 * out of the message body would not mean anything. Read it through
	 * `canEditCommunicationContent()` rather than directly, so the role half of the rule is not
	 * reimplemented per screen.
	 */
	public get messageTemplateStaffCanEditContent() {
		return this.isConfigEnabled(AppConfigKeys.messageTemplateStaffCanEditContent);
	}

	public get smsEnabled() {
		return this.isConfigEnabled(AppConfigKeys.smsEnabled);
	}

	public get staffCanSkipPayment() {
		return this.isConfigEnabled(AppConfigKeys.staffCanSkipPayment);
	}

	public get trailersEnabled() {
		return this.isConfigEnabled(AppConfigKeys.trailersEnabled);
	}

	public get trailerRemovalFeeEnabled() {
		return this.isConfigEnabled(AppConfigKeys.trailerRemovalFeeEnabled);
	}

	public get trailerHasOwnPricing() {
		return this.isConfigEnabled(AppConfigKeys.trailerHasOwnPricing);
	}

	// Whether or not a vehicle booking requires a passenger in the booking
	public get vehicleBookingRequiresAdult() {
		return this.isConfigEnabled(AppConfigKeys.vehicleBookingRequiresAdult);
	}

	// The minimum required passengers in a vehicle booking
	public get minRequiredAdultsInVehicleBooking() {
		return this.vehicleBookingRequiresAdult && this.vehicleHasPassengersEnabled ? 1 : 0;
	}

	// Whether or no passenger only bookings are enabled
	public get passengerOnlyBookingEnabled() {
		return this.isConfigEnabled(AppConfigKeys.passengerOnlyBookingsEnabled);
	}

	// Whether customers can create passenger-only bookings online (from the home page). When off,
	// customers can't start a passenger-only booking, but staff can still create them in the wizard
	// and check-in, and anytime tickets still work. Requires passengerOnlyBookingEnabled.
	public get passengerOnlyCustomerBookingFlowEnabled() {
		return this.isConfigEnabled(AppConfigKeys.passengerOnlyCustomerBookingFlowEnabled);
	}

	// Whether the passenger-only booking option should be offered to the current viewer.
	// Staff always get it when passenger-only bookings are enabled; customers additionally require
	// the customer online flow flag. When passenger-only bookings are disabled, nobody gets it.
	// This is the single source of truth for showing the walk-on option across the home page and
	// booking wizard. (Check-in is a staff-only context and reads passengerOnlyBookingEnabled directly.)
	public get showPassengerBookingOption() {
		return this.passengerOnlyBookingEnabled
			&& (this.passengerOnlyCustomerBookingFlowEnabled || store.isStaff);
	}

	// Whethere or not to show the passenger selection for vehicle booking
	public get vehicleHasPassengersEnabled() {
		return this.isConfigEnabled(AppConfigKeys.vehicleHasPassengersEnabled);
	}

	public get showTransactionPopUp() {
		return this.isConfigEnabled(AppConfigKeys.showConfirmationPopUpInCheckIn);
	}

	public get regoScanningEnabled() {
		return this.isConfigEnabled(AppConfigKeys.regoScanningEnabled);
	}

	public get twoFactorRequiredOnAllStaffAccounts() {
		return this.isConfigEnabled(AppConfigKeys.twoFactorRequiredOnAllStaffAccounts);
	}

	public get UnpaidBookingsEnabled() {
		return this.isConfigEnabled(AppConfigKeys.allowUnpaidBookings);
	}

	public get ShowUnpaidStatus() {
		return this.isConfigEnabled(AppConfigKeys.showUnpaidStatus);
	}

	/** Whether the Unpaid status/tag should be shown — when either creating unpaid bookings is
	 *  allowed, or the dedicated "show unpaid status" setting is on (e.g. to surface legacy unpaid
	 *  bookings after disabling new pay-later bookings). */
	public get ShowUnpaidStatusTag() {
		return this.UnpaidBookingsEnabled || this.ShowUnpaidStatus;
	}

	public get blockCheckInUntilPaid() {
		return this.isConfigEnabled(AppConfigKeys.blockCheckInUntilPaid);
	}

	public get manualRefundNoteRequired() {
		return this.isConfigEnabled(AppConfigKeys.requireManualRefundNote);
	}

	/** Whether cancellation reasons are collected at all, for either actor. Gates the bookings-table
	 *  and export column — there is nothing to show a column of if nobody is ever asked. */
	public get cancellationReasonsEnabled() {
		return (
			this.isConfigEnabled(AppConfigKeys.cancellationReasonStaffEnabled)
			|| this.isConfigEnabled(AppConfigKeys.cancellationReasonCustomerEnabled)
		);
	}

	public get refundsEnabledInCheckIn() {
		return this.isConfigEnabled(AppConfigKeys.allowRefundsInCheckIn);
	}

	public get CalendarStartDay() {
		const value = +(this.config?.find(x => x.key === AppConfigKeys.calendarWeekStartDay)?.value ?? '1');
		return !Number.isNaN(value) && value >= 0 && value <= 6 ? value : 1;
	}

	public get DriverlessVehicleBookingPriceIncludesAdultPrice() {
		return this.isConfigEnabled(AppConfigKeys.driverlessVehicleIncludesOneAdultPrice);
	}

	public get MaxNumberOfWaitlistItems() {
		return this.config.find(x => x.key === AppConfigKeys.maxNumberOfWaitlistItems)?.value ?? 20;
	}

	public get standbyEnabled() {
		return this.isConfigEnabled(AppConfigKeys.standbyEnabled);
	}

	public get standbyOpenHoursBeforeDeparture(): number {
		const defaultHours = 48;
		const value =
			this.config?.find(x => x.key === AppConfigKeys.standbyOpenHoursBeforeDeparture)?.value ?? defaultHours;
		const hours = +value;
		if (Number.isNaN(hours)) {
			return defaultHours;
		}
		return hours;
	}

	public get standybyMaxCountPerTrip(): number {
		const defaultMaxCount = 100;
		const value = this.config?.find(x => x.key === AppConfigKeys.standbyMaxCountPerTrip)?.value ?? defaultMaxCount;
		const maxCount = +value;
		if (Number.isNaN(maxCount)) {
			return defaultMaxCount;
		}
		return maxCount;
	}

	public get standbyAuthorizedUserTypes(): string[] {
		const value = this.config?.find(x => x.key === AppConfigKeys.standbyAuthorizedUserTypes)?.value;
		if (value) {
			return value.split(',').filter(x => x.trim() !== '');
		}
		return [];
	}

	public get standbyCargoCharacteristicWatchlist(): string[] {
		const value = this.config?.find(x => x.key === AppConfigKeys.standbyCargoCharacteristicsWatchlist)?.value;
		if (value) {
			return value.split(',');
		}
		return [];
	}

	public get showCreditOnCustomerAccount() {
		return this.isConfigEnabled(AppConfigKeys.showCreditsTabOnCustomerAccount);
	}

	public get allowManualCreditCreation() {
		return this.isConfigEnabled(AppConfigKeys.allowManualCreditCreation);
	}

	public get checkInCanEditDriverNames() {
		return this.isConfigEnabled(AppConfigKeys.checkInCanEditDriverNames);
	}

	public get guestBookingsEnabled() {
		return this.isConfigEnabled(AppConfigKeys.guestBookingsEnabled);
	}

	public get systemWasMigrated() {
		return this.isConfigEnabled(AppConfigKeys.systemWasMigrated);
	}

	public get giftCertificateExpiryYears(): number {
		const defaultYears = 5;
		const value =
			this.config?.find(x => x.key === AppConfigKeys.voucherPurchasedExpiryYears)?.value ?? defaultYears;
		const years = +value;
		return Number.isNaN(years) ? defaultYears : years;
	}

	public get creditVoucherExpiryYears(): number {
		const defaultYears = 1;
		const value = this.config?.find(x => x.key === AppConfigKeys.voucherCreditExpiryYears)?.value ?? defaultYears;
		const years = +value;
		return Number.isNaN(years) ? defaultYears : years;
	}

	private static readonly DEFAULT_VOUCHER_REDEEM_TEXT = [
		'<p>How to redeem:</p>',
		'<ul>',
		'<li>- Use the voucher number at the Cart page next time you book with us.</li>',
		'<li>- You can use this voucher to make multiple bookings if you have credit remaining.</li>',
		'<li>- Voucher must be used before expiry.</li>',
		'</ul>',
	].join('');

	public get voucherRedeemText(): string {
		return (
			this.config?.find(x => x.key === AppConfigKeys.voucherRedeemText)?.value ??
			AppConfigStore.DEFAULT_VOUCHER_REDEEM_TEXT
		);
	}

	public get globalBannerEnabled(): boolean {
		return this.isConfigEnabled(AppConfigKeys.globalBannerEnabled);
	}

	public get globalBannerContent(): string {
		return this.config?.find(x => x.key === AppConfigKeys.globalBannerContent)?.value ?? '';
	}

	public get marketingEnabled(): boolean {
		return this.isConfigEnabled(AppConfigKeys.marketingEnabled);
	}

	public get vehicleExactWeightInputEnabled(): boolean {
		return this.isConfigEnabled(AppConfigKeys.weightExactInputVehicleEnabled);
	}

	public get trailerExactWeightInputEnabled(): boolean {
		return this.isConfigEnabled(AppConfigKeys.weightExactInputTrailerEnabled);
	}

	private getCharacteristicIdList(key: string): string[] {
		const value = this.config?.find(x => x.key === key)?.value;
		if (!value) return [];
		return value
			.split(',')
			.map(x => x.trim())
			.filter(x => x !== '');
	}

	public get vehicleExactWeightRequiredCharacteristicIds(): string[] {
		return this.getCharacteristicIdList(AppConfigKeys.weightExactInputVehicleRequiredCharacteristicIds);
	}

	public get trailerExactWeightRequiredCharacteristicIds(): string[] {
		return this.getCharacteristicIdList(AppConfigKeys.weightExactInputTrailerRequiredCharacteristicIds);
	}

	/**
	 * Resolves the configured maximum exact weight for vehicles, converted to
	 * kg. Returns null when no cap is set (blank value, non-positive, or
	 * unparseable). Customer wizard validation rejects entries above this.
	 */
	public get vehicleExactWeightMaximumKg(): number | null {
		return this.getExactWeightMaximumKg(
			AppConfigKeys.weightExactInputVehicleMaximumValue,
			AppConfigKeys.weightExactInputVehicleMaximumUnit,
		);
	}

	public get trailerExactWeightMaximumKg(): number | null {
		return this.getExactWeightMaximumKg(
			AppConfigKeys.weightExactInputTrailerMaximumValue,
			AppConfigKeys.weightExactInputTrailerMaximumUnit,
		);
	}

	/**
	 * Resolves the configured maximum combined vehicle + trailer exact weight,
	 * converted to kg. Returns null when no cap is set. Only meaningful when a
	 * booking has both a vehicle and a trailer exact weight.
	 */
	public get combinedExactWeightMaximumKg(): number | null {
		return this.getExactWeightMaximumKg(
			AppConfigKeys.weightExactInputCombinedMaximumValue,
			AppConfigKeys.weightExactInputCombinedMaximumUnit,
		);
	}

	private getExactWeightMaximumKg(valueKey: string, unitKey: string): number | null {
		const raw = this.config?.find(x => x.key === valueKey)?.value?.trim();
		if (!raw) return null;
		const value = Number(raw);
		if (!Number.isFinite(value) || value <= 0) return null;
		const unit = parseWeightUnit(this.config?.find(x => x.key === unitKey)?.value?.trim() ?? 'kg');
		return toCanonicalKg(value, unit);
	}

	/**
	 * Resolves the display unit for stored weights. Explicit override via
	 * Weight.displayUnit wins; otherwise we derive from
	 * ConfigGeneral.SystemOfMeasurement (METRIC → kg, IMPERIAL → lb).
	 */
	public get weightDisplayUnit(): WeightUnit {
		const override = this.config?.find(x => x.key === AppConfigKeys.weightDisplayUnit)?.value?.trim();
		if (isWeightUnit(override)) return override;
		return whiteLabelStore?.systemMeasurementUnit === 'IMPERIAL' ? 'lb' : 'kg';
	}

	/**
	 * Returns true when the customer must enter an exact weight for a vehicle
	 * of the given cargo type. Master switch off → false. Empty characteristic
	 * list + master on → true for all. Non-empty list → true only when the
	 * cargo type has a matching characteristic id.
	 */
	public cargoTypeRequiresExactWeight(cargoTypeCharacteristicIds: string[] | null | undefined): boolean {
		if (!this.vehicleExactWeightInputEnabled) return false;
		const required = this.vehicleExactWeightRequiredCharacteristicIds;
		if (required.length === 0) return true;
		const ids = cargoTypeCharacteristicIds ?? [];
		return ids.some(id => required.includes(id));
	}

	public towOnTypeRequiresExactWeight(towOnTypeCharacteristicIds: string[] | null | undefined): boolean {
		if (!this.trailerExactWeightInputEnabled) return false;
		const required = this.trailerExactWeightRequiredCharacteristicIds;
		if (required.length === 0) return true;
		const ids = towOnTypeCharacteristicIds ?? [];
		return ids.some(id => required.includes(id));
	}
}

export const appConfigStore = new AppConfigStore(window.configData);
